// ModuleImageCarousel.jsx - For Home Page and Module Preview
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Star, Clock, Trophy } from 'lucide-react';

// Local keyboard navigation hook
const useKeyboardNav = (currentIndex, totalItems, setCurrentIndex, options = {}) => {
  const { orientation = 'horizontal', loop = false, onActivate } = options;

  return (event) => {
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';

    if (event.key === nextKey) {
      event.preventDefault();
      setCurrentIndex((prev) => {
        const next = prev + 1;
        return loop ? next % totalItems : Math.min(next, totalItems - 1);
      });
    } else if (event.key === prevKey) {
      event.preventDefault();
      setCurrentIndex((prev) => {
        const next = prev - 1;
        return loop ? (next + totalItems) % totalItems : Math.max(next, 0);
      });
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (onActivate) onActivate();
    }
  };
};

const ModuleImageCarousel = ({ 
  modules, 
  onModuleClick,
  autoPlay = true,
  autoPlayInterval = 5000,
  showControls = true,
  showDots = true,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isFocused, setIsFocused] = useState(false);

  // Keyboard navigation handler
  const handleKeyDown = useKeyboardNav(
    currentIndex,
    modules.length,
    setCurrentIndex,
    {
      orientation: 'horizontal',
      loop: true,
      onActivate: () => setIsPlaying(!isPlaying),
    }
  );

  // Auto-play functionality - pause when focused or manually paused
  useEffect(() => {
    if (!isPlaying || !autoPlay || isFocused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % modules.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isPlaying, autoPlay, isFocused, autoPlayInterval, modules.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + modules.length) % modules.length);
    setIsPlaying(false); // Pause auto-play when user interacts
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % modules.length);
    setIsPlaying(false); // Pause auto-play when user interacts
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPlaying(false);
  };

  const currentModule = modules[currentIndex];

  if (!modules || modules.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative w-full ${className}`}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      tabIndex="0"
      role="region"
      aria-label="Module carousel"
      aria-roledescription="carousel"
    >
      {/* Main Carousel Container */}
      <div className="relative h-96 md:h-[950px] overflow-hidden rounded-2xl" aria-live="polite" aria-atomic="false">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            {/* Background Image */}
            <div className="relative w-full h-full">
              <img 
                src={currentModule.image}
                alt={currentModule.imageAlt || `${currentModule.title} illustration`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-transparent" />
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex items-center">
                <div className="p-8 md:p-12 max-w-2xl">
                  {/* Module Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold">{currentModule.id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-purple-500/80 text-white text-sm rounded-full font-medium">
                        {currentModule.level}
                      </span>
                      <span className="text-white/80 text-sm">{currentModule.series} Series</span>
                    </div>
                  </div>

                  {/* Title and Subtitle */}
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                    {currentModule.title}
                  </h3>
                  <p className="text-xl text-purple-200 mb-4 font-medium">
                    {currentModule.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-slate-300 mb-6 text-lg leading-relaxed">
                    {currentModule.description}
                  </p>

                  {/* Module Stats */}
                  <div className="flex items-center gap-6 mb-6 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="w-4 h-4" />
                      <span>{currentModule.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Trophy className="w-4 h-4" />
                      <span>{currentModule.xpReward} XP</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Star className="w-4 h-4" />
                      <span>{currentModule.coolFactor}</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    onClick={() => onModuleClick?.(currentModule)}
                    className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="w-5 h-5" />
                    <span>Start Learning</span>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Controls */}
        {showControls && modules.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              aria-label="Previous module"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
            >
              <ChevronLeft className="w-6 h-6" aria-hidden="true" />
            </button>
            <button
              onClick={goToNext}
              aria-label="Next module"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
            >
              <ChevronRight className="w-6 h-6" aria-hidden="true" />
            </button>
          </>
        )}

        {/* Play/Pause Button */}
        {autoPlay && (
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            aria-label={isPlaying ? 'Pause carousel' : 'Resume carousel'}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
          >
            <span aria-hidden="true">{isPlaying ? '⏸️' : '▶️'}</span>
          </button>
        )}
      </div>

      {/* Dot Indicators */}
      {showDots && modules.length > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {modules.map((module, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              aria-label={`Go to module ${index + 1}: ${module.title}`}
              aria-current={index === currentIndex ? 'true' : 'false'}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-purple-500 scale-125'
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
            />
          ))}
        </div>
      )}

      {/* Module Thumbnails (Optional) */}
      <div className="mt-6 hidden md:flex gap-4 overflow-x-auto pb-2">
        {modules.map((module, index) => (
          <motion.button
            key={module.id}
            onClick={() => goToSlide(index)}
            aria-label={`Go to module: ${module.title}`}
            aria-current={index === currentIndex ? 'true' : 'false'}
            className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
              index === currentIndex
                ? 'border-purple-500 shadow-lg'
                : 'border-slate-600 hover:border-slate-500'
            }`}
            whileHover={{ scale: 1.05 }}
          >
            <img
              src={module.image}
              alt={`${module.title} thumbnail`}
              className="w-full h-full object-cover"
            />
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default ModuleImageCarousel;